import { SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from 'react';
import { useFetcher, useLoaderData, useParams } from "react-router-dom";
import { TelephoneBoards } from "../../components/NavDrawer/NavDrawerModels";
import { arraysAreEqual, packObjForSubmit, unpackObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchPut } from "../../utils/doFetchPut";
import getUser from "../../utils/getUser";
import { TelephonePlugIn } from "./TelephonePlugIn";
import { IOption, IPlugInRouteData, IPreference, ISlipsData, IToggle } from "./TelephonePlugInData";
import { OptionCode, TIME_FORMAT, ToggleCode, ToggleDetailsCode, defaultErrorMsg } from "./TelephonePlugInModels";

export const TelephonePlugInRouteLoader = async ({ request, params }: { request: any, params: any }) => {
  let data: any = {};
  let shiftsData: { shifts: { shift: string; shiftDate: string; }[] } = { shifts: [] };
  let slipsData: ISlipsData = { slips: null };
  if (params.board && params.shift && params.date) {
    shiftsData = await doFetch(`telephone-plugin/shifts/${params.board}`, { signal: request.signal }, true, false);
    data = await doFetch(`telephone-plugin/${params.board}/${params.shift}/${params.date}`, { signal: request.signal }, true, false);
    slipsData = await doFetch(`telephone-plugin/slips/${params.board}/${params.shift}/${params.date}`, { signal: request.signal }, true, false);
  } else if (params.board) {
    shiftsData = await doFetch(`telephone-plugin/shifts/${params.board}`, { signal: request.signal }, true, false);
    const [{ shift, shiftDate: date }] = shiftsData.shifts;
    const convertedDate = date.replace(/\./g, '');
    const convertedShift = shift.replace(':', '');
    data = await doFetch(`telephone-plugin/${params.board}/${convertedShift}/${convertedDate}`, { signal: request.signal }, true, false);
    slipsData = await doFetch(`telephone-plugin/slips/${params.board}/${convertedShift}/${convertedDate}`, { signal: request.signal }, true, false);
  }
  return {
    data,
    shifts: shiftsData.shifts.map(s => ({
      display: dayjs(`${s.shift.replace(':', '')} ${s.shiftDate.replace(/\./g, '')}`, "HHmm DDMMYYYY").format(TIME_FORMAT),
      value: `${s.shift.replace(':', '')},${s.shiftDate.replace(/\./g, '')}`
    })),
    slips: slipsData.slips
  }
}

export const TelephonePlugInRouteAction = async ({ request, params }: { request: any, params: any }) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  return doFetchPut(
    `telephone-plugin/${params.board}`,
    { signal: request.signal },
    {
      board: params.board,
      shift: obj.shift,
      date: obj.date,
      ...(obj.options && ({ options: obj.options })),
      ...(obj.toggles && ({ toggles: obj.toggles })),
      ...(obj.preferenceCode1 && ({ preferenceCode1: obj.preferenceCode1 })),
      ...(obj.preferenceCode2 && ({ preferenceCode2: obj.preferenceCode2 })),
      ...(obj.preferenceCode3 && ({ preferenceCode3: obj.preferenceCode3 })),
      isPluggedIn: obj.isPluggedIn,
      ...(obj.toggleDetailSequence?.length && ({ toggleDetailSequence: obj.toggleDetailSequence })),
      ...(obj.toggleDetailSequence?.length && {
        toggleDetails: obj.toggleDetailSequence.reduce((sum: number, tds: number | undefined) =>
          sum + (tds ?? 0), 0)
      }),
      hasChangesForTogglesOnly: obj.hasChangesForTogglesOnly
    }
  );
}

// eslint-disable-next-line max-statements
export const TelephonePlugInRoute = () => {
  const loaderData = useLoaderData();
  const fetcher = useFetcher();

  const { data, shifts = [], slips } = fetcher.data?.data ? fetcher.data as IPlugInRouteData :
    loaderData as IPlugInRouteData;

  const { board } = useParams();
  const user = getUser();
  const initialShift = shifts[0] ? shifts[0].value : '';
  const [selectedShift, setSelectedShift] = useState<string | undefined>(initialShift);
  const [currShift, setCurrShift] = useState<string>(initialShift);
  const [plugInFor, setPlugInFor] = useState(data?.isPluginChecked || false);
  const [preferences, setPreferences] = useState<IPreference[]>(data?.userPreferences ?? []);
  const [options, setOptions] = useState<IOption[]>(data?.options ?? []);
  const [toggles, setToggles] = useState<IToggle[]>(data?.toggle ?? []);
  const [openLoseUpdates, setOpenLoseUpdates] = useState(false);
  const [openReviewModal, setOpenReviewModal] = useState<boolean>(false);
  const [notification, setNotification] = useState('');
  const [openPlateModal, setOpenPlateModal] = useState(false);
  const loading = fetcher.state === 'loading';

  const hasChanges =
    !arraysAreEqual(data?.userPreferences ?? [], preferences) ||
    data?.isPluginChecked !== plugInFor ||
    !arraysAreEqual(data?.options ?? [], options) ||
    !arraysAreEqual(data?.toggle ?? [], toggles);

  /// In addition to hasChanges, check if the changes are only for toggles
  /// The back-end needs this to differentiate toggle-change only from toggle+plugin requests
  const hasChangesForTogglesOnly =
    arraysAreEqual(data?.userPreferences ?? [], preferences) &&
    data?.isPluginChecked === plugInFor &&
    arraysAreEqual(data?.options ?? [], options) &&
    !arraysAreEqual(data?.toggle ?? [], toggles);

  useEffect(() => {
    if (fetcher.data?.errorExists) {
      setNotification(fetcher.data.errorMessage || defaultErrorMsg);
    }
    else if (!fetcher.data?.errorExists && fetcher.data?.confirmationId && !selectedShift) {
      const [newShift, newDate] = currShift.split(',');
      fetcher.load(`/telephone-plugin/${board}/${newShift}/${newDate}`);
      setSelectedShift(currShift);
    }
  }, [board, fetcher, setSelectedShift, currShift, selectedShift])

  useEffect(() => {
    if (data) {
      setPreferences(data.userPreferences ?? []);
      setPlugInFor(data.isPluginChecked);
      setOptions(data.options ?? []);
      setToggles(data.toggle ?? []);
    }
  }, [fetcher, data])

  const onShiftSelect = (event: SelectChangeEvent<string>) => {
    const changedShift = event.target.value !== currShift;
    if (hasChanges && changedShift) {
      setOpenLoseUpdates(true);
      setSelectedShift(event.target.value);
    } else if (changedShift) {
      const [newShift, newDate] = event.target.value.split(',');
      fetcher.load(`/telephone-plugin/${board}/${newShift}/${newDate}`);
      setCurrShift(event.target.value);
    }
  };

  const onSelectPreference = (
    e: React.SyntheticEvent<Element, Event>,
    val: IPreference | null,
    sequence: 1 | 2 | 3
  ) => {
    e.stopPropagation();
    const pref = data.preferences.find(p => p.code === val?.code);
    const prefToReplace = preferences.find(p => p.sequence === sequence);
    if (pref) {
      const selectedPref = { ...pref, sequence };

      // If there is an existing selected preference with the same sequence, replace it. Otherwise, add it to the selected preferences array
      setPreferences(previous => prefToReplace ?
        previous.map(p => p.sequence === sequence ? selectedPref : p) : [...previous, selectedPref]);
    }
  }

  const onDeletePreference = (sequence: 1 | 2 | 3) =>
    setPreferences(previous => previous.filter(p => p.sequence !== sequence));

  const onPlugInFor = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      setOptions(previous => previous.map(o => ({ ...o, isChecked: false })));
      setPlugInFor(false);
      setPreferences([]);
    }
    setPlugInFor(e.target.checked);
  }

  const onCloseLoseUpdates = () => {
    setOpenLoseUpdates(false);
  }

  const onContinueLoseUpdates = () => {
    if (data && selectedShift) {
      const [newShift, newDate] = selectedShift.split(',');
      fetcher.load(`/telephone-plugin/${board}/${newShift}/${newDate}`);
      setCurrShift(selectedShift);
      setOpenLoseUpdates(false);
    }
  }

  const onChangeOption = (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
    e.stopPropagation();

    setOptions(previous => previous.map((o: IOption) =>
      o.code === code ? { ...o, isChecked: e.target.checked } : o
    ));
  }

  const onChangeToggle = (code: string, newT: IToggle) =>
    setToggles(previous => previous.map((t: IToggle) =>
      t.code === code ? newT : t
    ));

  const getPreferenceCode = (sequence: 1 | 2 | 3): number | undefined =>
    preferences.find(p => p.sequence === sequence)?.code;

  const onSubmit = async () => {
    /**
     * To submit an options change, the WebApi expects a bit string (in base10 representation), such 
     * that each bit reflects a specific option and whether it is selected or not selected
     * ex. "010" or 2 => toggle "L"/"LINES" is selected. All other toggles are not selected
     * Similar logic is used for toggle change and toggle detail change
     */
    const optionsSum = options.reduce((sum, o) =>
      OptionCode[o.code] && o.isChecked ? sum + OptionCode[o.code] : sum, 0);

    const togglesSum = toggles.reduce((sum, t) =>
      ToggleCode[t.code] && t.toggleCodeChecked ? sum + ToggleCode[t.code] : sum, 0);

    const userPreferences: number | undefined[] = Array.from({ length: 3 }, (_, i) => {
      const matchingPreference = preferences.find(p => p.sequence === i + 1);
      return matchingPreference ? matchingPreference.code : undefined;
    }) as number | undefined[];

    const toggleDetailSequence: number[] = [];
    const toggleWithSubBoards = toggles.find(t => t.squamishSubBoards?.length)?.squamishSubBoards;
    if (toggleWithSubBoards) {
      toggleWithSubBoards.filter(sb => sb.isChecked).forEach(sb => {
        const index = parseInt(sb.sequence.slice(1, -1), 10) - 1;
        toggleDetailSequence[index] = ToggleDetailsCode[sb.description as keyof typeof ToggleDetailsCode]
      })
    }
    if (selectedShift) {
      const [shift, date] = currShift.split(',');
      const action = `/telephone-plugin/${board}`;

      const formData = packObjForSubmit({
        options: optionsSum,
        toggles: togglesSum,
        isPluggedIn: plugInFor,
        shift,
        date,
        userPreferences,
        preferenceCode1: getPreferenceCode(1),
        preferenceCode2: getPreferenceCode(2),
        preferenceCode3: getPreferenceCode(3),
        toggleDetailSequence,
        hasChangesForTogglesOnly
      });

      await fetcher.submit(formData, {
        method: "put",
        action
      });

      setSelectedShift('');
    }
  }

  const onCloseNotification = () => {
    setOpenReviewModal(false);
    setNotification('');
    if (currShift) {
      const [newShift, newDate] = currShift.split(',');
      fetcher.load(`/telephone-plugin/${board}/${newShift}/${newDate}`);
    }
  }

  const params = {
    data,
    shifts,
    onShiftSelect,
    onDeletePreference,
    onSelectPreference,
    selectedPreferences: preferences,
    plugInFor,
    onPlugInFor,
    options,
    toggles,
    onChangeOption,
    onChangeToggle,
    openLoseUpdates,
    loading,
    onCloseLoseUpdates,
    openReviewModal,
    onOpenReviewModal: (open: boolean) => setOpenReviewModal(open),
    hasChanges,
    onSubmit,
    notification,
    onCloseNotification,
    slips,
    shift: shifts.find(s => currShift ? s.value === currShift : s.value === selectedShift),
    board: board?.toUpperCase() as TelephoneBoards,
    onContinueLoseUpdates,
    regId: user?.profile.sub,
    openPlateModal,
    onOpenPlateModal: () => setOpenPlateModal(open => !open)
  }

  return (
    <TelephonePlugIn {...params} />
  )
}